/**
 * Event Logs Modal Component
 * 
 * Modal for displaying event logs for a specific event
 */

import { FC, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '@/components/Common/Modal';
import DataTable from '@/components/DataTable/DataTable';
import { Loading } from '@/components/Common/Loading';
import { StatusBadge } from '@/components/Common/StatusBadge';
import { createEventLogColumns } from '@/components/DataTable/eventLogColumns';
import { eventLogService } from '@/services';
import type { EventDetailsResponse, EventLogListItem } from '@/types';
import styles from './EventLogsModal.module.css';

interface EventLogsModalProps {
  open: boolean;
  onClose: () => void;
  event: EventDetailsResponse | null;
}

const EventLogsModal: FC<EventLogsModalProps> = ({ 
  open, 
  onClose, 
  event 
}) => {
  /**
   * Fetch event logs for the specific event using direct API endpoint
   */
  const {
    data: eventLog,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['event-log', event?.id],
    queryFn: () => event?.id ? eventLogService.getEventLogByAgentEventId(event.id) : Promise.resolve(null),
    enabled: !!event?.id && open,
    refetchInterval: 10000, // Refresh every 10 seconds when modal is open
  });

  // Convert single log to array for table consistency
  const eventLogs = eventLog ? [eventLog] : [];

  /**
   * Handle view event log detail (nested modal or redirect)
   */
  const handleViewLog = (eventLog: EventLogListItem) => {
    // Could open another modal or navigate to detail page
    console.log('View event log detail:', eventLog);
  };

  /**
   * Create columns without the agentEventId since we know it's for this specific event
   */
  const columns = createEventLogColumns({
    onView: handleViewLog,
  }).filter(col => col.id !== 'agentEventId'); // Remove agentEventId column since it's redundant

  /**
   * Reset query when modal closes
   */
  useEffect(() => {
    if (!open && event) {
      // Optional: could clear the cache when modal closes to save memory
    }
  }, [open, event]);

  if (!event) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Logs do Evento #${event.id}`}
      description={`Visualize todos os logs de processamento do evento ${event.eventType}`}
      size="xl"
    >
      <div className={styles.modalContent}>
        {/* Event Info Header */}
        <div className={styles.eventInfo}>
          <div className={styles.eventDetails}>
            <div className={styles.eventItem}>
              <label>ID do Evento</label>
              <span>#{event.id}</span>
            </div>
            <div className={styles.eventItem}>
              <label>Tipo</label>
              <span>{event.eventType}</span>
            </div>
            <div className={styles.eventItem}>
              <label>Status</label>
              <StatusBadge status={event.status} type="event" />
            </div>
            <div className={styles.eventItem}>
              <label>Agente</label>
              <span>{event.agentName || 'Sem nome'}</span>
            </div>
            <div className={styles.eventItem}>
              <label>Criado em</label>
              <span>{new Date(event.createdAt).toLocaleString('pt-BR')}</span>
            </div>
          </div>
        </div>

        {/* Event Logs Table */}
        <div className={styles.logsSection}>
          <div className={styles.logsSectionHeader}>
            <h3>Logs de Processamento</h3>
            <span className={styles.logsCount}>
              {eventLogs.length} {eventLogs.length === 1 ? 'log' : 'logs'} encontrado{eventLogs.length === 1 ? '' : 's'}
            </span>
          </div>

          {isLoading && (
            <div className={styles.loadingContainer}>
              <Loading size="md" />
              <span>Carregando logs do evento...</span>
            </div>
          )}

          {isError && (
            <div className={styles.errorContainer}>
              <span>Erro ao carregar logs: {error instanceof Error ? error.message : 'Erro desconhecido'}</span>
              <button onClick={() => refetch()} className={styles.retryButton}>
                Tentar novamente
              </button>
            </div>
          )}

          {!isLoading && !isError && (
            <div className={styles.tableContainer}>
              <DataTable
                data={eventLogs}
                columns={columns}
                isLoading={false}
                enableSorting={true}
                enablePagination={eventLogs.length > 10}
                emptyMessage={`Nenhum log encontrado para o evento #${event.id}`}
                showSkeleton={false}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EventLogsModal;